import { openDB, type IDBPDatabase } from 'idb';

import type {
  StoredThread,
  StoredMessage,
  StoredAttachment,
  DatabaseError,
} from '@/lib/assistant-cloud-local/types';

import { DB_NAME, DB_VERSION, STORES, createSchema } from './schema';

let dbInstance: IDBPDatabase | null = null;
let currentDbName = DB_NAME;
let recoveryAttempted = false;

function isDatabaseConnectionError(error: unknown): boolean {
  const message = extractErrorMessage(error);
  return (
    message.includes('Database deleted by request of the user') ||
    message.includes('UnknownError') ||
    message.includes('Internal error opening backing store') ||
    message.includes('Connection is closing') ||
    message.includes('database connection has been closed') ||
    message.includes('Transaction inactive')
  );
}

function isVersionMismatchError(error: unknown): boolean {
  const message = extractErrorMessage(error);
  return message.includes('less than the existing version');
}

async function silentCatch<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    console.error('[IndexedDB] silentCatch error:', extractErrorMessage(error));
    if (isDatabaseConnectionError(error)) {
      dbInstance = null;
      recoveryAttempted = false;
    }
    return fallback;
  }
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error instanceof Event) {
    const target = error.target as IDBRequest | IDBTransaction | null;
    const dbError = target?.error;
    return dbError?.message || `IndexedDB ${error.type} event`;
  }
  return String(error);
}

export async function checkStorageAccess(): Promise<boolean> {
  if (typeof window === 'undefined' || !('indexedDB' in window)) {
    return false;
  }

  try {
    const testDbName = '__storage_test__';
    const request = indexedDB.open(testDbName);

    const openResult = await new Promise<IDBDatabase | null>(resolve => {
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('test')) {
          db.createObjectStore('test');
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = event => {
        event.preventDefault();
        resolve(null);
      };
      request.onblocked = () => resolve(null);
    });

    if (!openResult) {
      return false;
    }

    const db = openResult;

    try {
      const tx = db.transaction('test', 'readwrite');
      const store = tx.objectStore('test');
      store.put('test_value', 'test_key');
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = event => {
          event.preventDefault();
          reject(tx.error);
        };
        tx.onabort = () => reject(new Error('Transaction aborted'));
      });

      const readTx = db.transaction('test', 'readonly');
      const readStore = readTx.objectStore('test');
      const getRequest = readStore.get('test_key');
      const value = await new Promise<unknown>((resolve, reject) => {
        getRequest.onsuccess = () => resolve(getRequest.result);
        getRequest.onerror = event => {
          event.preventDefault();
          reject(getRequest.error);
        };
      });

      db.close();
      indexedDB.deleteDatabase(testDbName);

      return value === 'test_value';
    } catch {
      db.close();
      return false;
    }
  } catch {
    return false;
  }
}

async function ensureAllStores(db: IDBPDatabase): Promise<IDBPDatabase> {
  const requiredStores = Object.values(STORES);
  const missingStores = requiredStores.filter(s => !db.objectStoreNames.contains(s));
  if (missingStores.length === 0) return db;

  const nextVersion = db.version + 1;
  console.warn('[IndexedDB] Missing stores:', missingStores, '— reopening at version', nextVersion);
  db.close();

  const upgraded = await openDB(currentDbName, nextVersion, {
    upgrade(upgradeDb) {
      createSchema(upgradeDb as unknown as IDBDatabase);
    },
    blocked: () => {
      console.error('[IndexedDB] Store repair blocked by another tab', currentDbName);
    },
    blocking: () => {
      console.warn('[IndexedDB] This tab is blocking a store repair', currentDbName);
      dbInstance?.close();
      dbInstance = null;
    },
    terminated: () => {
      console.error('[IndexedDB] Connection terminated during store repair', currentDbName);
      dbInstance = null;
    },
  });
  return upgraded;
}

export async function getDB(): Promise<IDBPDatabase> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
    const dbError: DatabaseError = {
      name: 'DatabaseError',
      message: 'IndexedDB is not available (SSR or unsupported browser)',
      code: 'DB_NOT_AVAILABLE',
      details: new Error('Not in browser environment'),
    };
    throw dbError;
  }

  if (dbInstance) {
    return dbInstance;
  }

  try {
    console.log('[IndexedDB] Opening database', currentDbName, 'version', DB_VERSION);
    dbInstance = await openDB(currentDbName, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        console.log('[IndexedDB] Upgrading from', oldVersion, 'to', newVersion);
        createSchema(db as unknown as IDBDatabase);
      },
      blocked: () => {
        console.error('[IndexedDB] Database upgrade blocked by another tab', currentDbName);
      },
      blocking: () => {
        console.warn('[IndexedDB] This tab is blocking a database upgrade', currentDbName);
        dbInstance?.close();
        dbInstance = null;
      },
      terminated: () => {
        console.error('[IndexedDB] Database connection terminated', currentDbName);
        dbInstance = null;
      },
    });

    console.log('[IndexedDB] Opened successfully, version', dbInstance.version);
    const validated = await ensureAllStores(dbInstance);
    dbInstance = validated;
    return validated;
  } catch (error) {
    // Handle DB previously opened at a higher version (e.g., from a reverted migration).
    // Reopen without version constraint to use the existing schema and preserve data.
    if (isVersionMismatchError(error)) {
      console.warn('[IndexedDB] Version mismatch, reopening at existing version');
      const reopened = await openDB(currentDbName);
      console.log('[IndexedDB] Reopened at version', reopened.version);
      const validatedReopened = await ensureAllStores(reopened);
      dbInstance = validatedReopened;
      return validatedReopened;
    }

    const shouldRecover = isDatabaseConnectionError(error) && !recoveryAttempted;

    if (shouldRecover) {
      recoveryAttempted = true;
      currentDbName = `${DB_NAME}-recovery-${Date.now()}`;
      console.warn('[IndexedDB] Database unavailable, creating fresh database', currentDbName);

      try {
        dbInstance = await openDB(currentDbName, DB_VERSION, {
          upgrade(db) {
            createSchema(db as unknown as IDBDatabase);
          },
        });
        return dbInstance;
      } catch (recoveryError) {
        console.error('[IndexedDB] Recovery failed', recoveryError);
        throw {
          name: 'DatabaseError',
          message: `Database recovery failed: ${extractErrorMessage(recoveryError)}`,
          code: 'DB_RECOVERY_ERROR',
          details: recoveryError,
        } as DatabaseError;
      }
    }

    throw {
      name: 'DatabaseError',
      message: `Failed to open database: ${extractErrorMessage(error)}`,
      code: 'DB_OPEN_ERROR',
      details: error,
    } as DatabaseError;
  }
}

export async function closeDB(): Promise<void> {
  dbInstance?.close();
  dbInstance = null;
}

export async function deleteDB(): Promise<void> {
  closeDB();

  if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(currentDbName);
      request.onsuccess = () => resolve();
      request.onerror = event => {
        event.preventDefault();
        reject(request.error);
      };
      request.onblocked = () => {
        console.warn('[IndexedDB] Database deletion blocked', currentDbName);
      };
    });
  }
}

export async function clearDB(): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    const stores = [STORES.THREADS, STORES.MESSAGES, STORES.ATTACHMENTS] as const;
    const tx = db.transaction([...stores], 'readwrite');
    for (const store of stores) {
      tx.objectStore(store).clear();
    }
    await tx.done;
  }, undefined);
}

export async function createThread(thread: StoredThread): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.add(STORES.THREADS, thread);
  }, undefined);
}

export async function getThread(id: string): Promise<StoredThread | undefined> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.get(STORES.THREADS, id);
  }, undefined);
}

export async function updateThread(id: string, updates: Partial<StoredThread>): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    const thread = await db.get(STORES.THREADS, id);
    if (!thread) {
      return;
    }
    await db.put(STORES.THREADS, { ...thread, ...updates, updated_at: new Date() });
  }, undefined);
}

export async function deleteThread(id: string): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    // Get message keys in a separate read transaction to avoid race conditions
    const messageKeys = await db.getAllKeysFromIndex(STORES.MESSAGES, 'by-thread', id);
    // Now perform all deletes in a single transaction
    const tx = db.transaction([STORES.THREADS, STORES.MESSAGES], 'readwrite');
    tx.objectStore(STORES.THREADS).delete(id);
    for (const key of messageKeys) {
      tx.objectStore(STORES.MESSAGES).delete(key);
    }
    await tx.done;
  }, undefined);
}

export async function listThreads(options?: { archived?: boolean }): Promise<StoredThread[]> {
  return silentCatch(async () => {
    const db = await getDB();
    const allThreads = await db.getAll(STORES.THREADS);

    const threads =
      options?.archived !== undefined
        ? allThreads.filter(t => t.is_archived === options.archived)
        : allThreads;

    return threads.sort((a, b) => {
      const dateA = a.updated_at instanceof Date ? a.updated_at : new Date(a.updated_at);
      const dateB = b.updated_at instanceof Date ? b.updated_at : new Date(b.updated_at);
      return dateB.getTime() - dateA.getTime();
    });
  }, []);
}

export async function createMessage(message: StoredMessage): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.add(STORES.MESSAGES, message);
  }, undefined);
}

export async function updateMessage(message: StoredMessage): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.put(STORES.MESSAGES, message);
  }, undefined);
}

export async function getMessage(id: string): Promise<StoredMessage | undefined> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.get(STORES.MESSAGES, id);
  }, undefined);
}

export async function listMessages(threadId: string): Promise<StoredMessage[]> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.getAllFromIndex(STORES.MESSAGES, 'by-thread', threadId);
  }, []);
}

export async function deleteMessage(id: string): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.delete(STORES.MESSAGES, id);
  }, undefined);
}

export async function deleteMessagesByThread(threadId: string): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    // Get keys in a separate read transaction to avoid race conditions
    const keys = await db.getAllKeysFromIndex(STORES.MESSAGES, 'by-thread', threadId);
    // Now perform all deletes in a single transaction
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    for (const key of keys) {
      tx.store.delete(key);
    }
    await tx.done;
  }, undefined);
}

export async function createAttachment(attachment: StoredAttachment): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.add(STORES.ATTACHMENTS, attachment);
  }, undefined);
}

export async function getAttachment(id: string): Promise<StoredAttachment | undefined> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.get(STORES.ATTACHMENTS, id);
  }, undefined);
}

export async function deleteAttachment(id: string): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.delete(STORES.ATTACHMENTS, id);
  }, undefined);
}

export async function listAttachments(): Promise<StoredAttachment[]> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.getAll(STORES.ATTACHMENTS);
  }, []);
}

export async function getStorageStats(): Promise<{
  threads: number;
  messages: number;
  attachments: number;
}> {
  return silentCatch(
    async () => {
      const db = await getDB();
      const [threads, messages, attachments] = await Promise.all([
        db.count(STORES.THREADS),
        db.count(STORES.MESSAGES),
        db.count(STORES.ATTACHMENTS),
      ]);
      return { threads, messages, attachments };
    },
    { threads: 0, messages: 0, attachments: 0 },
  );
}

export async function exportData(): Promise<{
  threads: StoredThread[];
  messages: StoredMessage[];
  attachments: Omit<StoredAttachment, 'data'>[];
}> {
  return silentCatch(
    async () => {
      const db = await getDB();
      const [threads, messages, attachments] = await Promise.all([
        db.getAll(STORES.THREADS),
        db.getAll(STORES.MESSAGES),
        db.getAll(STORES.ATTACHMENTS),
      ]);
      return {
        threads,
        messages,
        attachments: attachments.map(({ data: _data, ...rest }) => rest),
      };
    },
    { threads: [], messages: [], attachments: [] },
  );
}

export async function importData(data: {
  threads: StoredThread[];
  messages: StoredMessage[];
  attachments?: Omit<StoredAttachment, 'data'>[];
}): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    const tx = db.transaction([STORES.THREADS, STORES.MESSAGES, STORES.ATTACHMENTS], 'readwrite');

    // Queue all operations synchronously without awaiting to keep transaction alive
    tx.objectStore(STORES.THREADS).clear();
    tx.objectStore(STORES.MESSAGES).clear();
    tx.objectStore(STORES.ATTACHMENTS).clear();

    for (const thread of data.threads) {
      tx.objectStore(STORES.THREADS).add(thread);
    }
    for (const message of data.messages) {
      tx.objectStore(STORES.MESSAGES).add(message);
    }
    for (const attachment of data.attachments ?? []) {
      tx.objectStore(STORES.ATTACHMENTS).add({ ...attachment, data: new Blob() });
    }

    await tx.done;
  }, undefined);
}

export interface PendingDeletion {
  message_id: string;
  remote_id: string;
  thread_id: string;
  queued_at: Date;
}

export async function addPendingDeletion(
  threadId: string,
  messageId: string,
  remoteId: string,
): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.put(STORES.PENDING_DELETIONS, {
      message_id: messageId,
      remote_id: remoteId,
      thread_id: threadId,
      queued_at: new Date(),
    });
  }, undefined);
}

export async function getPendingDeletionsByThread(threadId: string): Promise<string[]> {
  return silentCatch(async () => {
    const db = await getDB();
    const deletions = await db.getAllFromIndex(STORES.PENDING_DELETIONS, 'by-thread', threadId);
    return deletions.map(d => d.remote_id);
  }, []);
}

export async function getAllPendingDeletions(): Promise<PendingDeletion[]> {
  return silentCatch(async () => {
    const db = await getDB();
    return db.getAll(STORES.PENDING_DELETIONS);
  }, []);
}

export async function removePendingDeletion(messageId: string): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.delete(STORES.PENDING_DELETIONS, messageId);
  }, undefined);
}

export async function clearPendingDeletions(): Promise<void> {
  return silentCatch(async () => {
    const db = await getDB();
    await db.clear(STORES.PENDING_DELETIONS);
  }, undefined);
}
